import { ReactElement } from 'react';

import { VisitDiagnosis, VisitLocation } from '@dreryk/egabinet-visit-frontend-api';

import { useFormController } from '@dreryk/react-common';
import { Form, Heading, StyledFlex } from '@dreryk/react-components';

import { useGetCurrentHealthcareProviderType } from '~libs.shared.account';

import { dischargeSummaryTranslationKeys } from '../../translations';

import { DischargeSummaryFormBean, DischargeSummaryFormController } from './DischargeSummaryForm.types';
import { StyledDischargeSummaryFormSection } from './DischargeSummaryFormSection.styled';
import { DiseaseField } from './fields/DiseaseField/DiseaseField';
import { FurtherMedicalTreatmentGuidelinesField } from './fields/FurtherMedicalTreatmentGuidelinesField/FurtherMedicalTreatmentGuidelinesField';
import { ProcedureField } from './fields/ProcedureField/ProcedureField';
import { ProvidedMedicalServiceDescriptionField } from './fields/ProvidedMedicalServiceDescriptionField/ProvidedMedicalServiceDescriptionField';
import { ReferringPhysicianGuidelinesTextareaField } from './fields/ReferringPhysicianGuidelinesTextareaField/ReferringPhysicianGuidelinesTextareaField';
import { SpecialtyField } from './fields/SpecialtyField/SpecialtyField';
import { MedicalDepartmentField } from './fields/MedicalDepartmentField/MedicalDepartmentField';
import { getDefaultValues, isHealthcareOrganization } from './DischargeSummaryForm.utils';

type DischargeSummaryChildrenRenderProps = Pick<
   DischargeSummaryFormController,
   'formState' | 'handleSubmit' | 'handleSubmitDraft' | 'reset' | 'trigger'
>;

type DischargeSummaryFormProps = {
   readonly defaultValues: DischargeSummaryFormBean | null;
   readonly children: (renderProps: DischargeSummaryChildrenRenderProps) => ReactElement;
   readonly dictionaryValidOn: Date;
   readonly visitDiagnoses: VisitDiagnosis[] | undefined;
   readonly visitLocation: VisitLocation;
};

// eslint-disable-next-line max-lines-per-function
export const DischargeSummaryForm = (props: DischargeSummaryFormProps): ReactElement => {
   const healthcareProviderType = useGetCurrentHealthcareProviderType();
   const {
      register,
      formState,
      handleSubmit,
      handleSubmitDraft,
      watch,
      reset,
      trigger,
      setValue,
   } = useFormController<DischargeSummaryFormBean>(getDefaultValues(props.defaultValues, props.visitDiagnoses));

   return (
      <Form>
         <Heading content={dischargeSummaryTranslationKeys.dischargeSummary} testId={'dischargeSummary'} type={'h1'} />
         <StyledFlex gap={'40px'} direction={'column'}>
            <StyledDischargeSummaryFormSection direction={'column'}>
               <StyledFlex direction={'column'} gap={'18px'}>
                  {isHealthcareOrganization(healthcareProviderType, props.visitLocation) ? (
                     <MedicalDepartmentField
                        register={register}
                        watch={watch}
                        setValue={setValue}
                        visitLocation={props.visitLocation}
                     />
                  ) : null}
                  <Heading
                     content={dischargeSummaryTranslationKeys.providedMedicalServiceDescription}
                     testId={'dischargeSummary'}
                     type={'h2'}
                  />
               </StyledFlex>
               <StyledFlex gap={'24px'} direction={'column'}>
                  <DiseaseField
                     visitDiagnoses={props.visitDiagnoses}
                     dictionaryValidOn={props.dictionaryValidOn}
                     register={register}
                  />
                  <ProcedureField dictionaryValidOn={props.dictionaryValidOn} register={register} />
                  <ProvidedMedicalServiceDescriptionField register={register} />
               </StyledFlex>
            </StyledDischargeSummaryFormSection>
            <StyledDischargeSummaryFormSection direction={'column'}>
               <Heading
                  content={dischargeSummaryTranslationKeys.furtherMedicalTreatmentGuidelines.sectionHeader}
                  testId={'furtherMedicalTreatmentGuidelines'}
                  type={'h2'}
               />
               <StyledFlex gap={'24px'} direction={'column'}>
                  <FurtherMedicalTreatmentGuidelinesField register={register} />
                  <SpecialtyField register={register} watch={watch} />
               </StyledFlex>
            </StyledDischargeSummaryFormSection>
            <StyledDischargeSummaryFormSection direction={'column'}>
               <Heading
                  content={dischargeSummaryTranslationKeys.referringPhysicianGuidelines}
                  testId={'referringPhysicianGuidelines'}
                  type={'h2'}
               />
               <StyledFlex gap={'24px'} direction={'column'}>
                  <ReferringPhysicianGuidelinesTextareaField register={register} name={'diagnosticsGuidelines'} />
                  <ReferringPhysicianGuidelinesTextareaField register={register} name={'pharmacotherapyGuidelines'} />
                  <ReferringPhysicianGuidelinesTextareaField register={register} name={'otherGuidelines'} />
               </StyledFlex>
            </StyledDischargeSummaryFormSection>
         </StyledFlex>
         {props.children({
            formState: formState,
            handleSubmit: handleSubmit,
            handleSubmitDraft: handleSubmitDraft,
            reset: reset,
            trigger: trigger,
         })}
      </Form>
   );
};
